import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Alert } from 'react-bootstrap';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { API_BASE_URL } from '../config/api';

const COLORS = [
  '#2563eb', '#16a34a', '#dc2626', '#d97706', '#7c3aed',
  '#0891b2', '#be185d', '#65a30d', '#ea580c', '#4338ca',
];

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const MONTH_LABELS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function getDefaultStartDate() {
  const now = new Date();
  let month = now.getMonth() + 1 - 3;
  let year = now.getFullYear();
  if (month <= 0) {
    month += 12;
    year -= 1;
  }
  return { month, year };
}

export function LocationRevenueChart() {
  const now = new Date();
  const defaultStart = getDefaultStartDate();

  const [startMonth, setStartMonth] = useState(defaultStart.month);
  const [startYear, setStartYear] = useState(defaultStart.year);
  const [endMonth, setEndMonth] = useState(now.getMonth() + 1);
  const [endYear, setEndYear] = useState(now.getFullYear());

  const [chartData, setChartData] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const yearOptions = [];
  for (let i = 2025; i <= now.getFullYear(); i++) {
    yearOptions.push(i);
  }

  const isRangeValid = () => {
    return startYear < endYear || (startYear === endYear && startMonth <= endMonth);
  };

  const getMonthsInRange = () => {
    const months = [];
    let y = startYear;
    let m = startMonth;
    while (y < endYear || (y === endYear && m <= endMonth)) {
      months.push({ year: y, month: m });
      m += 1;
      if (m > 12) {
        m = 1;
        y += 1;
      }
    }
    return months.slice(0, 24);
  };

  useEffect(() => {
    if (!isRangeValid()) return;
    fetchChartData();
  }, [startMonth, startYear, endMonth, endYear]);

  const fetchChartData = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('tegrat_auth_token');
      const months = getMonthsInRange();

      const responses = await Promise.all(
        months.map(({ year, month }) =>
          fetch(
            `${API_BASE_URL}/analytics/stores/comparison?year=${year}&month=${month}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            }
          ).then((r) => (r.ok ? r.json() : Promise.reject(new Error('Request failed'))))
        )
      );

      const storeNameSet = new Set();
      const dataPoints = responses.map((data, i) => {
        const { year, month } = months[i];
        const point = { month: `${MONTH_NAMES[month - 1]} ${year}` };
        (data.stores || []).forEach((store) => {
          const name = store.store_name;
          storeNameSet.add(name);
          point[name] =
            parseFloat(store.rental_revenue || 0) +
            parseFloat(store.rto_revenue || 0);
        });
        return point;
      });

      setStores(Array.from(storeNameSet));
      setChartData(dataPoints);
    } catch (err) {
      console.error('Error loading revenue chart data:', err);
      setError('Failed to load revenue data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container fluid className="py-2">
      <Row className="mb-4 align-items-center">
        <Col>
          <h3>Location Revenue Over Time</h3>
        </Col>
      </Row>

      <Card className="mb-4">
        <Card.Body>
          <Row className="align-items-end g-2">
            <Col xs="auto">
              <Form.Label>Start Month</Form.Label>
              <Form.Select
                value={startMonth}
                onChange={(e) => setStartMonth(parseInt(e.target.value))}
              >
                {MONTH_LABELS.map((name, idx) => (
                  <option key={idx + 1} value={idx + 1}>{name}</option>
                ))}
              </Form.Select>
            </Col>
            <Col xs="auto">
              <Form.Label>Start Year</Form.Label>
              <Form.Select
                value={startYear}
                onChange={(e) => setStartYear(parseInt(e.target.value))}
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </Form.Select>
            </Col>
            <Col xs="auto" className="pb-2">
              <span className="text-muted">to</span>
            </Col>
            <Col xs="auto">
              <Form.Label>End Month</Form.Label>
              <Form.Select
                value={endMonth}
                onChange={(e) => setEndMonth(parseInt(e.target.value))}
              >
                {MONTH_LABELS.map((name, idx) => (
                  <option key={idx + 1} value={idx + 1}>{name}</option>
                ))}
              </Form.Select>
            </Col>
            <Col xs="auto">
              <Form.Label>End Year</Form.Label>
              <Form.Select
                value={endYear}
                onChange={(e) => setEndYear(parseInt(e.target.value))}
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </Form.Select>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {!isRangeValid() && (
        <Alert variant="warning">End date must be on or after start date.</Alert>
      )}

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading revenue data...</p>
        </div>
      ) : (
        <Card>
          <Card.Body>
            {chartData.length === 0 ? (
              <p className="text-muted text-center py-4">No data available for selected range.</p>
            ) : (
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                  <Legend />
                  {stores.map((store, i) => (
                    <Line
                      key={store}
                      type="monotone"
                      dataKey={store}
                      stroke={COLORS[i % COLORS.length]}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            )}
          </Card.Body>
        </Card>
      )}
    </Container>
  );
}

export default LocationRevenueChart;
